import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import { StatusCandidato } from '../../../model/Candidato/Candidato';
import Processo, { StatusProcesso } from '../../../model/processo/Processo';
import { useBreadcrumbs } from '../../../Routes/BreadcrumbContext';
import { ProcessoViewModel } from '../../../viewmodel/ProcessoViewModel';
import ConfirmacaoEnvioModal from './ConfirmacaoEnvioModal';
import './EmailCandidatosHeteroidentificacao.css';
import HelpDrawerEmail from './HelpDrawerEmail';
import { BsQuestionCircle } from 'react-icons/bs';

interface PaginatedResponse<T> {
    content: T[];
    pageable: {
        pageNumber: number;
        pageSize: number;
        sort: {
            empty: boolean;
            unsorted: boolean;
            sorted: boolean;
        };
        offset: number;
        paged: boolean;
        unpaged: boolean;
    };
    last: boolean;
    totalPages: number;
    totalElements: number;
    size: number;
    number: number;
    sort: {
        empty: boolean;
        unsorted: boolean;
        sorted: boolean;
    };
    first: boolean;
    numberOfElements: number;
    empty: boolean;
}

interface CandidatoOnlyResponse {
    id: string;
    candidatoEditais: CandidatoEditalResponse[];
    nome: string;
    nomeSocial: string | null;
    cpf: string;
    passaporte: string | null;
    email: string;
    status: StatusCandidato;
    candidatoResponsavel: CandidatoResponsavelResponse | null;
    duplicidade: boolean;
    emailConvocacaoEnviado: boolean | null;
    emailConvocacaoConfirmado: boolean | null;
}

interface CandidatoEditalResponse {
    id: string;
    inscricao: string;
    candidatoId: string;
    editalId: string;
    nomeEdital: string;
}

interface CandidatoResponsavelResponse {
    id: string;
    nome: string;
    nomeSocial: string | null;
    cpf: string;
    passaporte: string | null;
    email: string;
}

const EmailCandidatosHeteroidentificacao: React.FC = () => {
    const { processoId } = useParams<{ processoId: string }>();
    const { setBreadcrumbs } = useBreadcrumbs();

    const [processo, setProcesso] = useState<Processo>();
    const [candidatos, setCandidatos] = useState<CandidatoOnlyResponse[]>([]);
    const [candidatosSelecionados, setCandidatosSelecionados] = useState<CandidatoOnlyResponse[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [totalElements, setTotalElements] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [randomSelectCount, setRandomSelectCount] = useState<number>(0);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [enviarParaTodos, setEnviarParaTodos] = useState<boolean>(false);
    const [datasBancas, setDatasBancas] = useState<any[]>([]);
    const [dataSelecionada, setDataSelecionada] = useState<string>('');
    const [showHelpDrawer, setShowHelpDrawer] = useState(false);

    const itemsPerPage = 20; // Usando o mesmo tamanho que o backend
    const maxPageNumbers = 5;
    
    const podeEnviarConvocacao = processo?.status !== StatusProcesso.FINALIZADO && processo?.status !== StatusProcesso.CANCELADO;

    useEffect(() => {
        setBreadcrumbs([
            { path: '/', breadcrumb: '🏠' },
            { path: '/processos', breadcrumb: 'Processos' },
            { path: `/processos/${processoId}/email-candidatos-heteroidentificacao`, breadcrumb: 'Email de Heteroidentificação' },
        ]);
    }, [processoId, setBreadcrumbs]);

    useEffect(() => {
        const fetchProcesso = async () => {
            try {
                const processoRecebido = await ProcessoViewModel.obterProcessoPorId(processoId!);
                setProcesso(processoRecebido);
            } catch (error) {
                console.error('Erro ao obter processo:', error);
            }
        };

        const fetchDatasBancas = async () => {
            try {
                const response = await api.get(`/processos/${processoId}/datas-bancas-disponiveis`);
                setDatasBancas(response.data);
            } catch (error) {
                console.error('Erro ao obter datas de bancas:', error);
            }
        };

        if (processoId) {
            fetchProcesso();
            fetchCandidatos(0);
            fetchDatasBancas();
        }
    }, [processoId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setCurrentPage(0);
            fetchCandidatos(0);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchCandidatos(currentPage);
    }, [currentPage]);

    const fetchCandidatos = async (page: number) => {
        setLoading(true);
        try {
            const response = await api.get<PaginatedResponse<CandidatoOnlyResponse>>(
                `/processos/${processoId}/candidatos`,
                {
                    params: {
                        page: page,
                        size: itemsPerPage,
                        sort: 'nome',
                        ascending: true,
                        ...(searchTerm && { search: searchTerm })
                    }
                }
            );

            setCandidatos(response.data.content);
            setTotalElements(response.data.totalElements);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.error('Erro ao obter candidatos:', error);
            toast.error('Erro ao carregar candidatos');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = () => {
        const todosNaPaginaSelecionados = candidatos.length > 0 && candidatos.every(candidato =>
            candidatosSelecionados.some(selected => selected.id === candidato.id)
        );
        
        if (todosNaPaginaSelecionados) {
            setCandidatosSelecionados(candidatosSelecionados.filter(
                selected => !candidatos.some(candidato => candidato.id === selected.id)
            ));
        } else {
            setCandidatosSelecionados([...candidatosSelecionados, ...candidatos.filter(
                candidato => !candidatosSelecionados.some(selected => selected.id === candidato.id)
            )]);
        }
    };

    const handleCheckboxChange = (candidato: CandidatoOnlyResponse) => {
        if (candidatosSelecionados.some(item => item.id === candidato.id)) {
            setCandidatosSelecionados(candidatosSelecionados.filter(item => item.id !== candidato.id));
        } else {
            setCandidatosSelecionados([...candidatosSelecionados, candidato]);
        }
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber - 1);
    };

    const handleFirstPage = () => {
        setCurrentPage(0);
    };

    const handleLastPage = () => {
        setCurrentPage(totalPages - 1);
    };

    const handlePreviousPage = () => {
        setCurrentPage(prevPage => Math.max(prevPage - 1, 0));
    };

    const handleNextPage = () => {
        setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages - 1));
    };

    const handleRandomSelect = () => {
        if (randomSelectCount <= 0) return;

        // Buscar todos os candidatos para seleção aleatória
        const fetchAllCandidatos = async () => {
            try {
                const response = await api.get<PaginatedResponse<CandidatoOnlyResponse>>(
                    `/processos/${processoId}/candidatos`,
                    {
                        params: {
                            page: 0,
                            size: totalElements,
                            sort: 'nome',
                            ascending: true
                        }
                    }
                );

                const allCandidatos = response.data.content;
                const shuffledCandidatos = [...allCandidatos].sort(() => 0.5 - Math.random());
                const selectedCandidatos = shuffledCandidatos.slice(0, Math.min(randomSelectCount, allCandidatos.length));
                setCandidatosSelecionados(selectedCandidatos);

            } catch (error) {
                console.error('Erro ao buscar todos os candidatos:', error);
                toast.error('Erro ao selecionar candidatos aleatoriamente');
            }
        };

        fetchAllCandidatos();
    };

    const getPageNumbers = () => {
        const currentPageDisplay = currentPage + 1;
        let startPage = Math.max(1, currentPageDisplay - Math.floor(maxPageNumbers / 2));
        const endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);

        if (endPage - startPage < maxPageNumbers - 1) {
            startPage = Math.max(1, endPage - maxPageNumbers + 1);
        }

        return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    };

    const handleOpenModal = (todos: boolean = false) => {
        setEnviarParaTodos(todos);
        setShowConfirmModal(true);
    };

    const handleCloseModal = () => {
        setShowConfirmModal(false);
        setEnviarParaTodos(false);
    };

    const handleConfirmModal = async () => {
        try {
            const params = new URLSearchParams({ processoId: processoId! });
            if (dataSelecionada) {
                params.append('dataBancaId', dataSelecionada);
            }

            let candidatosParaEnviar: CandidatoOnlyResponse[];
            if (enviarParaTodos) {
                const response = await api.get<PaginatedResponse<CandidatoOnlyResponse>>(
                    `/processos/${processoId}/candidatos`,
                    {
                        params: {
                            page: 0,
                            size: totalElements,
                            sort: 'nome',
                            ascending: true
                        }
                    }
                );
                candidatosParaEnviar = response.data.content;
            } else {
                candidatosParaEnviar = candidatosSelecionados;
            }

            const candidatosElegiveis = candidatosParaEnviar.filter(c => c.status !== StatusCandidato.VALIDADO);
            const candidatosValidados = candidatosParaEnviar.filter(c => c.status === StatusCandidato.VALIDADO);
            const candidatosComEmail = candidatosElegiveis.filter(c => c.email);
            const candidatosSemEmail = candidatosElegiveis.filter(c => !c.email);

            if (candidatosValidados.length > 0) {
                toast.warning(`${candidatosValidados.length} candidato(s) com status VALIDADO serão ignorados`);
            }
            if (candidatosSemEmail.length > 0) {
                toast.warning(`${candidatosSemEmail.length} candidato(s) sem email serão ignorados`);
            }

            if (candidatosComEmail.length === 0) {
                toast.error('Nenhum candidato elegível com email para enviar');
                return;
            }

            const ids = candidatosComEmail.map((candidato) => candidato.id);
            await api.post(`/emails/convocacao/candidatos?${params.toString()}`, ids);
            toast.success(`Emails enviados com sucesso para ${candidatosComEmail.length} candidato(s)!`);
            fetchCandidatos(currentPage);
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setShowConfirmModal(false);
            setEnviarParaTodos(false);
        }
    };

    if (!podeEnviarConvocacao) {
        return (
            <div className="email-candidatos-container">
                <h2>Enviar Email de Heteroidentificação aos Candidatos - Convocação</h2>
                <div className="alert alert-warning">
                    <strong>Atenção:</strong> Não é possível enviar convocações para processos finalizados ou cancelados.
                </div>
                <div className="info-container">
                    <span><strong>Edital:</strong> {processo?.nome}</span>
                    <span style={{ marginLeft: '40px' }}><strong>Status:</strong> {processo?.status}</span>
                </div>
            </div>
        );
    }

    return (
        <HelpDrawerEmail isOpen={showHelpDrawer} onClose={() => setShowHelpDrawer(false)}>
        <div className="email-candidatos-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0 }}>Enviar Email de Heteroidentificação aos Candidatos - Convocação</h2>
                <button
                    className="btn-pagination"
                    onClick={() => setShowHelpDrawer(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                >
                    <BsQuestionCircle /> Ajuda
                </button>
            </div>

            <div className="info-container">
                <span><strong>Edital:</strong> {processo?.nome}</span>
                <span style={{ marginLeft: '40px' }}><strong>Status:</strong> {processo?.status}</span>
                <span style={{ marginLeft: '40px' }}><strong>Candidatos:</strong> {totalElements}</span>
                <span style={{ marginLeft: '40px' }}><strong>Página:</strong> {currentPage + 1} de {totalPages}</span>
                <span style={{ marginLeft: '40px', color: '#007bff' }}><strong>Selecionados:</strong> {candidatosSelecionados.length}</span>
            </div>

            {datasBancas.length > 0 && (
                <div style={{
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #007bff',
                    borderRadius: '8px',
                    padding: '20px',
                    marginTop: '20px',
                    marginBottom: '20px'
                }}>
                    <Form.Group>
                        <Form.Label style={{ fontSize: '16px', fontWeight: 'bold', color: '#007bff', marginBottom: '10px' }}>
                            📅 Selecione a Data da Banca para Convocação:
                        </Form.Label>
                        <Form.Select
                            value={dataSelecionada}
                            onChange={(e) => setDataSelecionada(e.target.value)}
                            style={{ fontSize: '15px', padding: '10px' }}
                        >
                            <option value="">Nenhuma data específica (opcional)</option>
                            {datasBancas.map((dataBanca) => (
                                <option key={dataBanca.id} value={dataBanca.id}>
                                    {dataBanca.data.split('-').reverse().join('/')} - {dataBanca.horaInicial} às {dataBanca.horaFinal}
                                </option>
                            ))}
                        </Form.Select>
                        {dataSelecionada && (
                            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#d1ecf1', borderRadius: '5px', color: '#0c5460' }}>
                                ✓ Data selecionada: {datasBancas.find(d => d.id === dataSelecionada) && 
                                    `${datasBancas.find(d => d.id === dataSelecionada)!.data.split('-').reverse().join('/')} - 
                                    ${datasBancas.find(d => d.id === dataSelecionada)!.horaInicial} às 
                                    ${datasBancas.find(d => d.id === dataSelecionada)!.horaFinal}`}
                            </div>
                        )}
                    </Form.Group>
                </div>
            )}

            <div className="search-container">
                <Form.Control
                    type="text"
                    placeholder="Pesquisar por nome ou email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="table-container">
                <table className="candidatos-table">
                    <thead>
                        <tr>
                            <th>Nome</th>
                            <th>Nome Social</th>
                            <th>Email</th>
                            <th>Status Email</th>
                            <th>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'center' }}>
                                    <Form.Check
                                        type="checkbox"
                                        checked={candidatos.length > 0 && candidatos.every(candidato =>
                                            candidatosSelecionados.some(selected => selected.id === candidato.id)
                                        )}
                                        onChange={handleSelectAll}
                                    />
                                    <span style={{ color: '#666', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                        (Página atual: {candidatos.length})
                                    </span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {candidatos.map(candidato => (
                            <tr key={candidato.id}>
                                <td>{candidato.nome}</td>
                                <td>{candidato.nomeSocial || <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>}</td>
                                <td>{candidato.email || <span style={{ color: '#999', fontStyle: 'italic' }}>Sem email</span>}</td>
                                <td>
                                    {!candidato.email ? (
                                        <span style={{ color: '#999' }}>N/A</span>
                                    ) : candidato.emailConvocacaoEnviado ? (
                                        candidato.emailConvocacaoConfirmado ? (
                                            <span style={{ color: 'green' }}>✓ Enviado e confirmado</span>
                                        ) : (
                                            <span style={{ color: 'orange' }}>⚠ Enviado, aguardando confirmação</span>
                                        )
                                    ) : (
                                        <span style={{ color: 'red' }}>✗ Não enviado</span>
                                    )}
                                </td>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        checked={candidatosSelecionados.some(item => item.id === candidato.id)}
                                        onChange={() => handleCheckboxChange(candidato)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {loading && (
                <div className="loading-container">
                    <div className="spinner-border" role="status"></div>
                    <span>Carregando candidatos...</span>
                </div>
            )}

            {candidatos.length === 0 && !loading && (
                <div className="no-results">
                    <p>Nenhum candidato encontrado.</p>
                </div>
            )}

            {totalPages > 1 && (
                <div className="pagination-container">
                    <button
                        className="btn-pagination"
                        onClick={handleFirstPage}
                        disabled={currentPage === 0}
                    >
                        «
                    </button>
                    <button
                        className="btn-pagination"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 0}
                    >
                        ‹
                    </button>
                    {getPageNumbers().map(page => (
                        <button
                            key={page}
                            className={`btn-pagination ${page === currentPage + 1 ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        className="btn-pagination"
                        onClick={handleNextPage}
                        disabled={currentPage >= totalPages - 1}
                    >
                        ›
                    </button>
                    <button
                        className="btn-pagination"
                        onClick={handleLastPage}
                        disabled={currentPage >= totalPages - 1}
                    >
                        »
                    </button>
                </div>
            )}

            <div className="actions-container">
                <div className="random-select-container">
                    <Form.Control
                        type="number"
                        placeholder="Quantidade"
                        value={randomSelectCount}
                        onChange={(e) => setRandomSelectCount(Math.max(0, Number(e.target.value)))}
                        min={1}
                        max={totalElements}
                        style={{ width: '100px' }}
                    />
                    <button
                        className="btn-pagination"
                        onClick={handleRandomSelect}
                        disabled={randomSelectCount <= 0 || randomSelectCount > totalElements}
                        style={{
                            backgroundColor: randomSelectCount <= 0 || randomSelectCount > totalElements ? '#6c757d' : '#007bff',
                            color: 'white',
                            border: '1px solid #007bff'
                        }}
                    >
                        Selecionar Aleatoriamente
                    </button>
                    <button
                        className="btn-pagination"
                        onClick={() => setCandidatosSelecionados([])}
                        style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: '1px solid #dc3545',
                            marginLeft: '10px'
                        }}
                        disabled={candidatosSelecionados.length === 0}
                    >
                        Limpar Seleção
                    </button>
                </div>
                <div className="buttons-container">
                    <button
                        className="btn-pagination"
                        onClick={() => handleOpenModal(true)}
                        style={{ backgroundColor: '#ffc107', color: '#000', border: '1px solid #ffc107' }}
                    >
                        Enviar para Todos ({totalElements})
                    </button>
                    <button
                        className="btn-pagination"
                        onClick={() => handleOpenModal(false)}
                        disabled={candidatosSelecionados.length === 0}
                        style={{
                            backgroundColor: candidatosSelecionados.length === 0 ? '#6c757d' : '#28a745',
                            color: 'white',
                            border: `1px solid ${candidatosSelecionados.length === 0 ? '#6c757d' : '#28a745'}`
                        }}
                    >
                        Enviar para Selecionados ({candidatosSelecionados.length})
                    </button>
                </div>
            </div>

            <ConfirmacaoEnvioModal
                show={showConfirmModal}
                handleClose={handleCloseModal}
                handleConfirm={handleConfirmModal}
                processo={processo!}
                candidatosSelecionados={candidatosSelecionados}
                enviarParaTodos={enviarParaTodos}
                totalElements={totalElements}
                dataSelecionada={dataSelecionada}
                datasBancas={datasBancas}
            />
        </div>
        </HelpDrawerEmail>
    );
};

export default EmailCandidatosHeteroidentificacao;